import React from 'react';
import { BrandLogo } from '../../../auth/BrandLogo';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { toRelativeLogoMediaUrl } from '../../../../lib/logoStorageUrl';

/**
 * Is this primary-logo URL the platform-default DEEP·ATOMIC brand
 * wordmark (`/datacrate-logo.svg`), as opposed to an uploaded customer
 * logo? The static asset paints the wordmark near-black (#14171A), so on a
 * DARK cover it must render as the theme-aware white `BrandLogo` rather than
 * a dark mark inside a white backing chip. Uploaded customer logos render as a
 * solid white silhouette on the transparent dark surface (R-84b).
 */
export function isDatacrateBrandWordmark(url: string | null | undefined): boolean {
  if (!url) return false;
  // Match the bare default path and any leading-slash / origin-prefixed form
  // (e.g. the asset served from a CDN host) without over-matching arbitrary
  // URLs that merely contain the filename as a substring.
  return url === DEFAULT_PRIMARY_COVER_LOGO_URL || /\/datacrate-logo\.svg(?:[?#].*)?$/.test(url);
}

/**
 * Treatment for user-uploaded customer logos on report covers.
 *
 * Covers render inside the paper-white report island (`data-theme-scope="light"`),
 * so most cover surfaces are light regardless of the app theme. The previous
 * implementation forced the mark to monochrome (`brightness-0` → black, or
 * `brightness-0 invert` → white), which DISTORTS the brand — a red CLAYCO
 * wordmark turned solid black/white. We now render the uploaded mark AS-IS
 * (its real colours) and, when the surrounding surface is genuinely DARK
 * (e.g. a HeroImageCover photo background), seat it on a light backing chip so
 * it keeps contrast without recolouring.
 *
 *   - `on-light` → logo unmodified, no chip (the cover surface is already light).
 *   - `on-dark`  → logo unmodified on an always-light backing chip.
 */
export function customerLogoImgClass(
  _tone: 'on-dark' | 'on-light',
  sizing = 'h-8 max-w-[150px]',
): string {
  // Render the uploaded asset at true colour (no brightness/invert filter) so
  // the brand stays correct. The `tone` argument is retained for call-site
  // compatibility; dark surfaces get a backing chip via <CoverCustomerLogo>.
  return `${sizing} object-contain`;
}

interface CoverCustomerLogoProps {
  src: string;
  alt: string;
  tone: 'on-dark' | 'on-light';
  className?: string;
  'data-testid'?: string;
}

export const CoverCustomerLogo: React.FC<CoverCustomerLogoProps> = ({
  src,
  alt,
  tone,
  className,
  'data-testid': testId,
}) => {
  const baseClass = className ?? customerLogoImgClass(tone);
  // Normalize any baked-in localhost/run.app origin to a same-origin relative
  // URL so the hosted logo loads in prod (fixes the mixed-content/connection-
  // refused logo on cross-org report covers).
  const safeSrc = toRelativeLogoMediaUrl(src);

  // On a DARK cover surface, render the uploaded mark as a solid WHITE
  // silhouette directly on the transparent surface (NO white backing chip),
  // matching the white DEEP·ATOMIC wordmark treatment. `brightness-0 invert`
  // collapses any colour to black then flips it to white while PRESERVING the
  // PNG/SVG alpha, so a transparent logo stays transparent-backed and reads as
  // white on the teal/dark cover. Product decision (R-84b): the owner prefers a
  // clean white logo over the brand-true colours-on-a-white-chip treatment.
  // Trade-off: multi-colour marks flatten to a white silhouette (fine for
  // wordmarks). On-light covers keep the logo at true colour, unchipped.
  if (tone === 'on-dark') {
    return (
      <img
        src={safeSrc}
        alt={alt}
        className={`${baseClass} brightness-0 invert`}
        data-testid={testId}
        data-logo-white="true"
      />
    );
  }

  return (
    <img
      src={safeSrc}
      alt={alt}
      className={baseClass}
      data-testid={testId}
    />
  );
};

interface CoverPrimaryLogoProps {
  src: string;
  alt: string;
  tone: 'on-dark' | 'on-light';
  /** Rendered height in CSS px — drives the white inline BrandLogo on dark. */
  heightPx: number;
  /** Sizing/utility classes applied to the uploaded-customer-logo <img>. */
  imgClassName?: string;
  'data-testid'?: string;
}

/**
 * Primary brand mark on a report cover.
 *
 * The previous implementation rendered EVERY primary logo (including the
 * platform-default DEEP·ATOMIC wordmark) as a static <img> seated on a white
 * backing chip whenever the cover surface was dark — producing a dark
 * wordmark inside a white box on the teal/dark hero cover.
 *
 * Fix: when the primary logo IS the DA brand wordmark (`/datacrate-logo.svg`)
 * AND the surface is dark, render the theme-aware inline `BrandLogo` instead.
 * `BrandLogo` paints the wordmark with `currentColor` pinned to
 * `text-text-primary`; scoping it `text-white` here renders the mark WHITE
 * directly on the transparent dark surface — no chip. On a LIGHT cover the
 * default wordmark keeps the plain <img> (its near-black artwork reads fine
 * on a pale background). Uploaded customer logos render white-on-transparent on
 * dark covers (R-84b) via `CoverCustomerLogo` (no backing chip).
 */
export const CoverPrimaryLogo: React.FC<CoverPrimaryLogoProps> = ({
  src,
  alt,
  tone,
  heightPx,
  imgClassName,
  'data-testid': testId,
}) => {
  if (tone === 'on-dark' && isDatacrateBrandWordmark(src)) {
    return (
      // White wordmark directly on the transparent dark surface (no chip).
      // Wrapper carries the call-site testid so selectors still resolve.
      <span className="inline-flex items-center" data-testid={testId} data-brand-wordmark="white">
        <BrandLogo height={heightPx} title={alt} className="text-white" />
      </span>
    );
  }
  return (
    <CoverCustomerLogo
      src={src}
      alt={alt}
      tone={tone}
      className={imgClassName}
      data-testid={testId}
    />
  );
};
